import React, { lazy, Suspense, useMemo, useState } from "react";
import { useCanPreviewPdf } from "../Shared/Pdf/useCanPreviewPdf.ts";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import { useLocale } from "../Shared/i18n/LocaleContext.tsx";
import { format } from "../Shared/i18n/format.ts";
import { newSeed } from "../Shared/Helpers.tsx";
import { Operations } from "../MathFactsGenerator/Constants.ts";
import { ladderFor, levelName } from "../MathFactsGenerator/FactLevels.ts";
import { CATCH_UP_MAX, catchUpCount, catchUpSheets } from "../MathFactsGenerator/CatchUp.ts";
import type { CatchUpChoice } from "../MathFactsGenerator/CatchUp.ts";
import { gradeName } from "../MathFactsGenerator/SubPack.ts";
import type { PackSpec } from "../Shared/Seo/routeSeo.ts";

// Behind the lazy boundary like every other PDF surface: the renderer loads only when a catch-up pack is
// asked for (bundleBoundary.test.js).
const WorksheetPreview = lazy(() => import("../MathFactsGenerator/PdfGenerator/WorksheetPreview.tsx"));

/**
 * The catch-up pack of a placement check (ADR 0086 §5): tick the skills a child missed, optionally pick a
 * level to practise facts from, and get a pack of those pages with a cover and the answers behind.
 *
 * What is ticked is component state, as the class roster is (0011 §1): it is a list of what a child missed,
 * so it is never stored, put in the link, or sent. It is also not in the pack's ID, so the page offers no
 * way to rebuild a catch-up pack from one; pressing the button again draws new problems for the same
 * choice. The pack is built when the button is pressed and is a snapshot of that choice.
 */
export function CatchUpSection({ pack }: { pack: PackSpec }) {
    const check = pack.check!;
    const t = useTranslation();
    const locale = useLocale();
    const canPreview = useCanPreviewPdf();
    const ladder = ladderFor(check.factFinder.operations as Operations[], 10)!;
    const [ticked, setTicked] = useState<number[]>([]);
    const [level, setLevel] = useState<number | null>(null);
    const [made, setMade] = useState<{ choice: CatchUpChoice; seed: string } | null>(null);

    const choice: CatchUpChoice = { strands: ticked, finderLevel: level };
    const count = catchUpCount(choice);
    const full = count >= CATCH_UP_MAX;
    const sheets = useMemo(() => (made ? catchUpSheets(pack, made.choice, made.seed) : []), [pack, made]);

    function toggle(index: number) {
        setTicked((current) => (current.includes(index) ? current.filter((i) => i !== index) : [...current, index]));
    }

    return (
        <section className="gradeHubSection catchUp">
            <h2>{t("settings.catchUpTitle")}</h2>
            <p className="classRosterHint">{t("settings.catchUpHint")}</p>
            <fieldset className="catchUpSkills">
                <legend>{t("settings.catchUpSkills")}</legend>
                {pack.activities.slice(0, check.stories + 1).map((activity, index) => {
                    const checked = ticked.includes(index);
                    return (
                        <label key={index} className="catchUpSkill">
                            <input
                                type="checkbox"
                                checked={checked}
                                disabled={full && !checked}
                                onChange={() => toggle(index)}
                            />{" "}
                            {activity.name ?? activity.slug}
                        </label>
                    );
                })}
            </fieldset>
            <div className="catchUpFact">
                <label htmlFor="catchUpLevel">{t("settings.catchUpFact")}</label>{" "}
                <select
                    id="catchUpLevel"
                    className="input"
                    value={level ?? ""}
                    disabled={full && level === null}
                    onChange={(event) => setLevel(event.target.value === "" ? null : Number(event.target.value))}
                >
                    <option value="">{t("settings.catchUpNoFact")}</option>
                    {ladder.levels.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                            {format(t("settings.catchUpLevelOption"), { n: entry.id, name: levelName(entry, ladder.id, locale) })}
                        </option>
                    ))}
                </select>
            </div>
            <p className="classRosterCount">{format(t("settings.catchUpCount"), { n: count, max: CATCH_UP_MAX })}</p>
            <button
                type="button"
                className="btn btn-primary"
                disabled={count < 1}
                onClick={() => setMade({ choice: { strands: [...ticked], finderLevel: level }, seed: newSeed() })}
            >
                {t("settings.catchUpMake")}
            </button>
            {made && (
                <div className="catchUpSheet">
                    <Suspense fallback={null}>
                        <WorksheetPreview
                            settings={sheets[0].settings}
                            seed={made.seed}
                            sheets={sheets}
                            canPreview={canPreview}
                            fileName={`${gradeName(pack.grade).replace(/ /g, "-")}-catch-up-practice.pdf`}
                            previewLabel={t("settings.catchUpTitle")}
                            eventParams={{ source: "catch_up_pack", operation: gradeName(pack.grade) }}
                        />
                    </Suspense>
                </div>
            )}
        </section>
    );
}

export default CatchUpSection;
